import React, {ChangeEvent, FormEvent, RefObject} from 'react';
import {formatFileSize} from '../utils';
import {MAX_POST_IMAGE_COUNT} from '../constants';
import HomeModalShell from './HomeModalShell';
import styles from '../Home.module.css';

interface CreatePostModalProps {
  isOpen: boolean;
  loading: boolean;
  error: string | null;
  validationError: string | null;
  statusMessage: string | null;
  uploadProgressPercent: number | null;
  content: string;
  selectedImageFiles: File[];
  selectedImagePreviewUrls: string[];
  textareaRef: RefObject<HTMLTextAreaElement>;
  fileInputRef: RefObject<HTMLInputElement>;
  onContentChange: (value: string) => void;
  onClose: () => void;
  onBrowseImage: () => void;
  onRemoveImage: (imageIndex: number) => void;
  onImageChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
}

const CreatePostModal: React.FC<CreatePostModalProps> = ({
  isOpen,
  loading,
  error,
  validationError,
  statusMessage,
  uploadProgressPercent,
  content,
  selectedImageFiles,
  selectedImagePreviewUrls,
  textareaRef,
  fileInputRef,
  onContentChange,
  onClose,
  onBrowseImage,
  onRemoveImage,
  onImageChange,
  onSubmit,
}) => {
  if (!isOpen) {
    return null;
  }

  const totalSelectedImageSize = selectedImageFiles.reduce((sum, selectedImageFile) => sum + selectedImageFile.size, 0);

  return (
    <HomeModalShell
      backdropClassName={styles.modalBackdrop}
      panelClassName={styles.modalCard}
      ariaLabelledBy="create-post-modal-title"
      ariaDescribedBy="create-post-modal-description"
      onClose={onClose}
    >
      <div className={styles.modalHeader}>
        <div>
          <h2 id="create-post-modal-title" className={styles.modalTitle}>Create a New Post</h2>
          <p id="create-post-modal-description" className={styles.modalDescription}>
            Share an update with your audience. Add text and up to four images before publishing.
          </p>
        </div>
        <button
          type="button"
          className={styles.modalClose}
          onClick={onClose}
          aria-label="Close create post modal"
        >
          ×
        </button>
      </div>

      <form onSubmit={onSubmit} className={styles.modalForm}>
        <label htmlFor="create-post-content" className={styles.modalLabel}>
          Post Content
        </label>
        <textarea
          id="create-post-content"
          ref={textareaRef}
          value={content}
          onChange={(event) => onContentChange(event.target.value)}
          className={styles.modalTextarea}
          placeholder="What do you want to share today?"
          maxLength={5000}
          disabled={loading}
        />

        <label htmlFor="create-post-image-file" className={styles.modalLabel}>
          Images
          <span className={styles.modalLabelHint}>{`Optional • Up to ${MAX_POST_IMAGE_COUNT}`}</span>
        </label>
        <input
          id="create-post-image-file"
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          className={styles.modalFileInput}
          onChange={onImageChange}
          disabled={loading}
        />
        <div className={styles.modalUploadRow}>
          <button
            type="button"
            className={styles.modalUploadButton}
            onClick={onBrowseImage}
            disabled={loading}
          >
            Browse Images
          </button>
          <div className={styles.modalUploadMeta}>
            <span className={styles.modalUploadName}>
              {selectedImageFiles.length === 0
                ? 'No images selected'
                : selectedImageFiles.length === 1
                  ? selectedImageFiles[0].name
                  : `${selectedImageFiles.length} images selected`}
            </span>
            <span className={styles.modalUploadHint}>
              {`JPG, PNG, WEBP up to 40 MB each, optimized automatically (${selectedImageFiles.length}/${MAX_POST_IMAGE_COUNT})`}
            </span>
          </div>
        </div>

        {selectedImagePreviewUrls.length > 0 && (
          <div className={styles.modalPreview}>
            <div className={styles.modalPreviewHeader}>
              <span>{selectedImageFiles.length === 1 ? 'Preview' : 'Previews'}</span>
              <span>{`${selectedImageFiles.length} image${selectedImageFiles.length === 1 ? '' : 's'} • ${formatFileSize(totalSelectedImageSize)}`}</span>
            </div>
            <div className={styles.modalPreviewGrid}>
              {selectedImagePreviewUrls.map((selectedImagePreviewUrl, imageIndex) => (
                <div key={`${selectedImageFiles[imageIndex]?.name || 'image'}-${imageIndex}`} className={styles.modalPreviewTile}>
                  <img
                    src={selectedImagePreviewUrl}
                    alt={`Selected post preview ${imageIndex + 1}`}
                    className={styles.modalPreviewTileImage}
                  />
                  <button
                    type="button"
                    className={styles.modalPreviewRemoveButton}
                    onClick={() => onRemoveImage(imageIndex)}
                    disabled={loading}
                    aria-label={`Remove image ${imageIndex + 1}`}
                  >
                    ×
                  </button>
                  <div className={styles.modalPreviewTileMeta} title={selectedImageFiles[imageIndex]?.name}>
                    {formatFileSize(selectedImageFiles[imageIndex]?.size || 0)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {validationError && (
          <div className={`${styles.modalMessage} ${styles.modalValidationMessage}`}>
            {validationError}
          </div>
        )}

        {statusMessage && (
          <div className={`${styles.modalMessage} ${styles.modalInfoMessage}`}>
            <div className={styles.modalStatusHeader}>
              <span>{statusMessage}</span>
              {typeof uploadProgressPercent === 'number' ? <span>{uploadProgressPercent}%</span> : null}
            </div>
            {typeof uploadProgressPercent === 'number' ? (
              <div className={styles.modalProgressTrack} aria-hidden="true">
                <div
                  className={styles.modalProgressFill}
                  style={{width: `${Math.min(Math.max(uploadProgressPercent, 0), 100)}%`}}
                />
              </div>
            ) : null}
          </div>
        )}

        {error && (
          <div className={`${styles.modalMessage} ${styles.modalErrorMessage}`}>
            {error}
          </div>
        )}

        <div className={styles.modalActions}>
          <button
            type="button"
            className={`${styles.modalButton} ${styles.modalButtonSecondary}`}
            onClick={onClose}
          >
            {loading ? 'Cancel Upload' : 'Cancel'}
          </button>
          <button
            type="submit"
            className={`${styles.modalButton} ${styles.modalButtonPrimary}`}
            disabled={loading}
          >
            {loading ? 'Posting...' : 'Create Post'}
          </button>
        </div>
      </form>
    </HomeModalShell>
  );
};

export default React.memo(CreatePostModal);
